/**
 * Страница «Настройки» — пароль, Telegram, email (отдельная страница, как редактирование мероприятия в админке).
 */

import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { useAuth } from "../../lib/auth.tsx";
import { getTelegram } from "../../lib/telegram.ts";
import { IconChevronRight, IconTelegram } from "../../components/Icons.tsx";
import { InstallSection } from "../../components/InstallPWAButton.tsx";

export function ProfileSettingsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  return (
    <Layout>
      <h1 className="page-title">Настройки</h1>
      <p className="text-[13px] text-slate-500 mb-4">
        Пароль, Telegram и email для входа
      </p>

      <div className="rounded-2xl bg-white/95 shadow-sm overflow-hidden">
        {account?.email ? (
          <Link
            to="/profile/password"
            className="flex items-center gap-3 p-4 border-b border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
          >
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Пароль</p>
              {account?.hasPassword ? (
                <p className="text-[13px] text-green-600">Установлен</p>
              ) : (
                <p className="text-[13px] text-slate-500">Не установлен</p>
              )}
            </div>
            <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </Link>
        ) : (
          <Link
            to="/profile/email"
            className="flex items-center gap-3 p-4 border-b border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
          >
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Пароль</p>
              <p className="text-[13px] text-slate-400">Сначала добавьте email</p>
            </div>
            <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </Link>
        )}

        {account?.telegramId ? (
          <div className="flex items-center gap-3 p-4 border-b border-slate-100">
            <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shrink-0">
              <IconTelegram className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Telegram</p>
              <p className="text-[13px] text-green-600">Подключён</p>
            </div>
            <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : (
          <a
            href={`https://t.me/${import.meta.env.VITE_BOT_USERNAME ?? "OneChurchBot"}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 border-b border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
          >
            <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shrink-0">
              <IconTelegram className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Telegram</p>
              <p className="text-[13px] text-slate-500">Не подключён</p>
            </div>
            <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </a>
        )}

        {account?.email ? (
          <div className="flex items-center gap-3 p-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Email</p>
              <p className="text-[13px] text-green-600 truncate">{account.email}</p>
            </div>
            <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : (
          <Link
            to="/profile/email"
            className="flex items-center gap-3 p-4 active:bg-slate-50 transition-colors no-underline text-inherit"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[15px] text-slate-800">Email</p>
              <p className="text-[13px] text-slate-500">Не подключён</p>
            </div>
            <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </Link>
        )}
      </div>

      {/* Добавить на главный экран — только в настройках */}
      <div className="mt-6">
        <InstallSection cardStyle />
      </div>
    </Layout>
  );
}
